/**
 * Error Logger Service
 *
 * Centralized error logging that routes to PostHog Error Tracking from both
 * client and server runtimes. Replaces the legacy window.Sentry path. The
 * public API (logError/logWarning/logInfo/setUser/clearContext) is preserved
 * so existing call sites keep working unchanged.
 */

import { classifyError, type ErrorContext } from "@/lib/utils/error-utils";

export interface ConversationContext {
  conversationId?: string;
  sessionId?: string;
  messageCount?: number;
  complexity?: string;
  sessionMode?: string;
  platformType?: string;
  workflowStage?: string;
  lastUserMessage?: string;
}

export interface ErrorLogOptions {
  conversation?: ConversationContext;
  component?: string;
  action?: string;
  organizationId?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
}

type PosthogJsClient = {
  captureException?: (err: unknown, properties?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  identify?: (distinctId: string, properties?: Record<string, unknown>) => void;
  reset?: () => void;
  group?: (groupType: string, groupKey: string, properties?: Record<string, unknown>) => void;
};

type PosthogNodeClient = {
  captureException?: (
    error: Error,
    distinctId?: string,
    additionalProperties?: Record<string, unknown>
  ) => void;
  capture?: (event: {
    distinctId: string;
    event: string;
    properties?: Record<string, unknown>;
    groups?: Record<string, string>;
  }) => void;
  shutdown?: () => Promise<void>;
};

// Lazy server import — keeps posthog-node out of the client bundle.
let serverClientPromise: Promise<PosthogNodeClient | null> | null = null;

async function getServerPostHog(): Promise<PosthogNodeClient | null> {
  if (typeof window !== "undefined") return null;
  if (serverClientPromise) return serverClientPromise;

  serverClientPromise = (async () => {
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) return null;
    try {
      const { PostHog } = await import("posthog-node");
      return new PostHog(key, {
        host:
          process.env.NEXT_PUBLIC_POSTHOG_HOST ??
          "https://us.i.posthog.com",
        flushAt: 1,
        flushInterval: 0,
      }) as unknown as PosthogNodeClient;
    } catch (err) {
      console.warn("[ErrorLogger] Failed to load posthog-node:", err);
      return null;
    }
  })();

  return serverClientPromise;
}

function getClientPostHog(): PosthogJsClient | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { posthog?: PosthogJsClient }).posthog ?? null;
}

function buildExceptionProperties(
  error: unknown,
  context: ErrorContext,
  options: ErrorLogOptions,
): Record<string, unknown> {
  const errObj = error instanceof Error ? error : new Error(String(error));
  const { conversation, component, action, organizationId, userId, metadata } =
    options;

  const props: Record<string, unknown> = {
    $exception_message: errObj.message,
    $exception_type: errObj.name,
    error_category: context.category,
    error_severity: context.severity,
    error_recoverable: context.recoverable,
    component: component ?? "unknown",
    action: action ?? "unknown",
    has_conversation: Boolean(conversation),
  };

  if (organizationId) props.organization_id = organizationId;
  if (userId) props.user_id = userId;

  if (conversation) {
    props.conversation = {
      conversation_id: conversation.conversationId,
      session_id: conversation.sessionId,
      message_count: conversation.messageCount,
      complexity: conversation.complexity,
      session_mode: conversation.sessionMode,
      platform_type: conversation.platformType,
      workflow_stage: conversation.workflowStage,
      last_user_message: conversation.lastUserMessage
        ? truncate(conversation.lastUserMessage, 200)
        : undefined,
    };
  }

  if (metadata && Object.keys(metadata).length > 0) {
    props.metadata = metadata;
  }

  return props;
}

function truncate(s: string, max: number): string {
  return s.length <= max ? s : `${s.slice(0, max)}...`;
}

function captureClientException(
  error: Error,
  properties: Record<string, unknown>,
): void {
  const ph = getClientPostHog();
  if (!ph) return;
  try {
    if (typeof ph.captureException === "function") {
      ph.captureException(error, properties);
    } else if (typeof ph.capture === "function") {
      ph.capture("$exception", properties);
    }
  } catch (err) {
    console.warn("[ErrorLogger] PostHog client capture failed:", err);
  }
}

function captureServerException(
  error: Error,
  properties: Record<string, unknown>,
  options: ErrorLogOptions,
): void {
  // Fire-and-forget — never block the caller on PostHog network latency.
  void (async () => {
    try {
      const client = await getServerPostHog();
      if (!client) return;
      const distinctId = options.userId ?? options.organizationId ?? "server";
      const groups = options.organizationId
        ? { organization: options.organizationId }
        : undefined;

      if (typeof client.captureException === "function") {
        client.captureException(error, distinctId, properties);
      } else if (typeof client.capture === "function") {
        client.capture({
          distinctId,
          event: "$exception",
          properties,
          groups,
        });
      }
      if (typeof client.shutdown === "function") {
        await client.shutdown();
      }
    } catch (err) {
      console.warn("[ErrorLogger] PostHog server capture failed:", err);
    }
  })();
}

/**
 * Main error logging service.
 */
export class ErrorLogger {
  private static instance: ErrorLogger;

  static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /** Log an exception with optional context. */
  logError(error: unknown, options: ErrorLogOptions = {}): void {
    const errObj = error instanceof Error ? error : new Error(String(error));
    const context = classifyError(error);
    const properties = buildExceptionProperties(errObj, context, options);

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error(
        `[ErrorLogger] ${options.component ?? "Unknown"}:`,
        { error: errObj, context, options },
      );
    }

    if (typeof window !== "undefined") {
      captureClientException(errObj, properties);
    } else {
      captureServerException(errObj, properties, options);
    }
  }

  /** Capture a warning as a non-fatal event. */
  logWarning(message: string, options: ErrorLogOptions = {}): void {
    const { component, metadata = {} } = options;

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(`[ErrorLogger] ${component ?? "Unknown"}:`, message, metadata);
    }

    const properties = {
      message,
      severity: "warning" as const,
      component: component ?? "unknown",
      ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
    };

    if (typeof window !== "undefined") {
      const ph = getClientPostHog();
      try {
        ph?.capture?.("error_logger_warning", properties);
      } catch {
        // ignore
      }
      return;
    }

    void (async () => {
      try {
        const client = await getServerPostHog();
        const distinctId = options.userId ?? options.organizationId ?? "server";
        const groups = options.organizationId
          ? { organization: options.organizationId }
          : undefined;
        client?.capture?.({
          distinctId,
          event: "error_logger_warning",
          properties,
          groups,
        });
        await client?.shutdown?.();
      } catch {
        // ignore
      }
    })();
  }

  /** Capture an info-level signal; only emitted when metadata.important is true. */
  logInfo(message: string, options: ErrorLogOptions = {}): void {
    const { component, metadata = {} } = options;

    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info(`[ErrorLogger] ${component ?? "Unknown"}:`, message, metadata);
    }

    if (!metadata.important) return;

    const properties = {
      message,
      severity: "info" as const,
      component: component ?? "unknown",
      metadata,
    };

    if (typeof window !== "undefined") {
      const ph = getClientPostHog();
      try {
        ph?.capture?.("error_logger_info", properties);
      } catch {
        // ignore
      }
      return;
    }

    void (async () => {
      try {
        const client = await getServerPostHog();
        const distinctId = options.userId ?? options.organizationId ?? "server";
        client?.capture?.({
          distinctId,
          event: "error_logger_info",
          properties,
        });
        await client?.shutdown?.();
      } catch {
        // ignore
      }
    })();
  }

  /**
   * Identify the current user for PostHog. On server runtimes this is a no-op —
   * server identification is handled by the calling request context.
   */
  setUser(userId: string | null, email?: string): void {
    if (typeof window === "undefined") return;
    const ph = getClientPostHog();
    if (!ph) return;
    try {
      if (userId) {
        ph.identify?.(userId, email ? { email } : undefined);
      } else {
        ph.reset?.();
      }
    } catch {
      // ignore — analytics must never crash the app
    }
  }

  /**
   * Clear conversation/component context. PostHog does not have a persistent
   * "context" model like Sentry — context is sent on each capture call — so
   * this is a no-op kept for API compatibility.
   */
  clearContext(): void {
    // No-op: PostHog contexts are per-event, not persistent.
  }

  /** Whether PostHog is reachable from this runtime. */
  isActive(): boolean {
    if (typeof window !== "undefined") {
      return Boolean(getClientPostHog());
    }
    return Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);
  }
}

// Singleton export.
export const errorLogger = ErrorLogger.getInstance();

/* ── Convenience wrappers ─────────────────────────────────────────────────── */

export function logChatError(
  error: unknown,
  conversationContext?: ConversationContext,
  metadata?: Record<string, unknown>,
): void {
  errorLogger.logError(error, {
    component: "ConversationFlow",
    conversation: conversationContext,
    metadata,
  });
}

export function logArtifactError(
  error: unknown,
  artifactType: string,
  metadata?: Record<string, unknown>,
): void {
  errorLogger.logError(error, {
    component: "ArtifactRenderer",
    action: `render_${artifactType}`,
    metadata: { ...metadata, artifactType },
  });
}

export function logMCPError(
  error: unknown,
  platform: string,
  operation: string,
  metadata?: Record<string, unknown>,
): void {
  errorLogger.logError(error, {
    component: "MCPExecution",
    action: operation,
    metadata: { ...metadata, platform, operation },
  });
}

export function logAPIError(
  error: unknown,
  endpoint: string,
  metadata?: Record<string, unknown>,
): void {
  errorLogger.logError(error, {
    component: "APIClient",
    action: `call_${endpoint}`,
    metadata: { ...metadata, endpoint },
  });
}
